import { useState } from "react";
import Layout from "../components/Layout";
import api, { downloadAuthedFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import PasswordInput from "../components/PasswordInput";
import { User, KeyRound, Mail, Wallet, Download } from "lucide-react";

/**
 * "My Profile" — every signed-in user can update their own display name and
 * email here, and change their password without involving an admin. Email
 * changes require re-typing the current password (sensitive op). Password
 * change requires the old password (anti-hijack).
 */
export default function Profile() {
  const { user, refreshMe } = useAuth();

  if (!user) return null;
  return (
    <Layout title="My Profile" subtitle="Your account">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-2xl space-y-7">
        <ProfileCard user={user} onSaved={refreshMe} />
        <PasswordCard />
        <PayslipCard user={user} />
      </div>
    </Layout>
  );
}

function ProfileCard({ user, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const emailChanged = email.trim().toLowerCase() !== (user.email || "").toLowerCase();

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    if (name.trim() && name.trim() !== user.name) payload.name = name.trim();
    if (emailChanged) {
      if (!currentPassword) {
        toast.error("Current password is required to change email");
        return;
      }
      payload.email = email.trim();
      payload.current_password = currentPassword;
    }
    if (Object.keys(payload).length === 0) {
      toast.info("Nothing to update");
      return;
    }
    setSubmitting(true);
    try {
      await api.put("/auth/me", payload);
      toast.success("Profile updated");
      setCurrentPassword("");
      await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="glass-card p-6 sm:p-7" data-testid="profile-card">
      <div className="flex items-start gap-4 mb-5">
        <div className="p-3 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05]">
          <User className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.25} />
        </div>
        <div>
          <div className="overline">Account</div>
          <h2 className="serif text-xl mt-1">Display name & email</h2>
          <div className="text-xs text-[#8A8A8A] mt-2">
            Role: <span className="text-[#D4AF37] uppercase tracking-wider mono">{user.role}</span>
            {user.is_super_admin && <span className="ml-2 text-[#C99356]">· super admin</span>}
          </div>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <div className="overline mb-2">Display name</div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="luxe-input w-full px-4 py-2.5 text-sm"
            data-testid="profile-name-input"
            required
          />
        </label>
        <label className="block">
          <div className="overline mb-2">Email</div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="profile-email-input"
            required
          />
        </label>
        {emailChanged && (
          <label className="block fade-up">
            <div className="overline mb-2 text-[#C99356]">Confirm current password (email change)</div>
            <PasswordInput
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm mono"
              data-testid="profile-current-password"
              autoComplete="current-password"
              placeholder="Your current password"
            />
          </label>
        )}
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="btn-gold px-5 py-2.5 inline-flex items-center gap-2"
            data-testid="profile-save"
          >
            <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
            {submitting ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </section>
  );
}

function PasswordCard() {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPw === oldPw) {
      toast.error("New password must be different from current");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/me/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password changed");
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="glass-card p-6 sm:p-7" data-testid="password-card">
      <div className="flex items-start gap-4 mb-5">
        <div className="p-3 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05]">
          <KeyRound className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.25} />
        </div>
        <div>
          <div className="overline">Security</div>
          <h2 className="serif text-xl mt-1">Change password</h2>
          <div className="text-xs text-[#8A8A8A] mt-2">
            You will stay signed in on this device. Other devices keep their session until logout.
          </div>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <div className="overline mb-2">Current password</div>
          <PasswordInput
            value={oldPw}
            onChange={(e) => setOldPw(e.target.value)}
            required
            autoComplete="current-password"
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="pw-old"
          />
        </label>
        <label className="block">
          <div className="overline mb-2">New password</div>
          <PasswordInput
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="pw-new"
            placeholder="At least 6 characters"
          />
        </label>
        <label className="block">
          <div className="overline mb-2">Confirm new password</div>
          <PasswordInput
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="pw-confirm"
          />
        </label>
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="btn-gold px-5 py-2.5"
            data-testid="pw-submit"
          >
            {submitting ? "Saving…" : "Change password"}
          </button>
        </div>
      </form>
    </section>
  );
}


function PayslipCard({ user }) {
  const [month, setMonth] = useState(currentMonth());
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const safeName = (user.name || "karyawan").replace(/\s+/g, "_").slice(0, 40);
      await downloadAuthedFile(
        `/payroll/users/${user.id}/payslip.pdf?month=${month}`,
        `slip_gaji_${safeName}_${month}.pdf`,
        { errorMessage: "Tidak dapat mengunduh slip gaji" },
      );
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="glass-card p-6 sm:p-7" data-testid="payslip-card">
      <div className="flex items-start gap-4 mb-5">
        <div className="p-3 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05]">
          <Wallet className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.25} />
        </div>
        <div>
          <div className="overline">Payroll</div>
          <h2 className="serif text-xl mt-1">Slip Gaji Saya</h2>
          <div className="text-xs text-[#8A8A8A] mt-2">
            Pilih periode untuk mengunduh slip gaji Anda (PDF · Bahasa Indonesia).
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <label className="block flex-1">
          <div className="overline mb-2">Periode</div>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="payslip-month"
          />
        </label>
        <button
          onClick={download}
          disabled={downloading}
          data-testid="payslip-download"
          className="btn-gold px-5 py-2.5 inline-flex items-center gap-2"
        >
          <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
          {downloading ? "Mengunduh…" : "Unduh Slip Gaji"}
        </button>
      </div>
    </section>
  );
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
